"use client";

import { FormControl, FormHelperText, InputLabelProps } from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import { ReactNode, useRef } from "react";
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import AppFromLabelField from "./AppFromLabelField";
import { useHandleUploadFile } from "@/hooks";
import { CategoryTypeUploadEnum } from "@/models";

const AppFormControlTextEditor = <T extends FieldValues>({
  defaultValue,
  label,
  control,
  name,
  rules,
  labelProps,
  onChangeValueForm,
  classNameContainer,
  controlProps,
  editorProps,
  helper,
  action,
  editorHeight = 500,
  categoryType,
  ownerId = "",
}: AppFormControlTextEditorProps<T>) => {
  const editorRef = useRef<any>(null);
  const { handleUploadFile } = useHandleUploadFile();

  return (
    <>
      <AppFromLabelField
        label={label}
        labelProps={{
          ...labelProps,
        }}
        className={classNameContainer}
        required={labelProps?.required}
        helper={helper}
        action={action}
      >
        <Controller
          control={control}
          name={name}
          rules={rules}
          {...controlProps}
          render={({
            field: { onChange, ...otherFieldProps },
            fieldState: { error: errorField },
          }) => {
            if (editorRef.current) {
              if (Boolean(errorField)) {
                editorRef.current.editorContainer.style.border =
                  "1px solid #ff234b";
              } else {
                editorRef.current.editorContainer.style.border =
                  "1px solid #d9d9d9";
              }
            }

            return (
              <FormControl
                error={!!errorField}
                margin="normal"
                className="m-0 block"
              >
                <Editor
                  onInit={(evt, editor) => (editorRef.current = editor)}
                  tinymceScriptSrc="/tinymce/tinymce.min.js"
                  licenseKey="gpl"
                  // value={defaultValue}
                  init={{
                    statusbar: false, // Ẩn thanh trạng thái, bao gồm cả dòng "Press Alt+0 for help"
                    // branding: false, // Ẩn dòng chữ "Build with TinyMCE"
                    height: editorHeight,
                    plugins: [
                      "advlist",
                      "autolink",
                      "link",
                      "image",
                      "media",
                      "lists",
                      "charmap",
                      "preview",
                      "anchor",
                      "pagebreak",
                      "searchreplace",
                      "wordcount",
                      "visualblocks",
                      "code",
                      "fullscreen",
                      "insertdatetime",
                      "media",
                      "table",
                      "emoticons",
                      "help",
                      // extraplugin
                      "eqneditor", // add
                      // "tiny_mce_wiris",
                      "mathjax",
                    ],
                    file_picker_callback: (callback, value, meta) => {
                      // Create an input element for file selection
                      let input = document.createElement("input");
                      input.setAttribute("type", "file");
                      if (meta.filetype === "image") {
                        input.setAttribute("accept", "image/*");
                      } else if (meta.filetype === "media") {
                        input.setAttribute("accept", "video/*");
                      }

                      input.onchange = async (e) => {
                        const target = e.target as HTMLInputElement;
                        const file = target.files ? target.files[0] : null;

                        if (file) {
                          const res = await handleUploadFile(
                            [file],
                            categoryType,
                            ownerId
                          );
                          if (res && res.length > 0) {
                            if (meta.filetype === "file") {
                              callback(res[0].link, {
                                alt: res[0].originName,
                              });
                            } else if (meta.filetype === "image") {
                              /* Provide image and alt text for the image dialog */
                              callback(res[0].link, {
                                alt: res[0].originName,
                              });
                            } else if (meta.filetype === "media") {
                              /* Provide alternative source and posted for the media dialog */
                              callback(
                                res[0].link
                                //   {
                                //   source2: "alt.ogg",
                                //   poster:
                                //     "https://www.google.com/logos/google.jpg",
                                // }
                              );
                            }
                          }
                        }
                      };
                      input.click(); // simulate a click on the input
                    },

                    toolbar:
                      "undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | " +
                      "bullist numlist outdent indent | link image | print preview media fullscreen | " +
                      "forecolor backcolor emoticons | help | mathjax eqneditor", // tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry
                    menu: {
                      favs: {
                        title: "Menu",
                        items: "code visualaid | searchreplace | emoticons",
                      },
                    },
                    menubar:
                      "favs file edit view insert format tools table help",
                    language: "vi",
                    mathjax: {
                      lib: "/mathjax/es5/tex-chtml.js", // Point to the MathJax library
                      symbols: {
                        start: "\\(", // Delimiters for inline equations
                        end: "\\)",
                      },
                      className: "mathjax", // Optional class name for an element containing a formula
                      // extensions: ["tex2jax.js"],
                      jax: ["input/TeX", "output/HTML-CSS"],
                      tex2jax: {
                        inlineMath: [
                          ["$", "$"],
                          ["\\(", "\\)"],
                        ],
                        displayMath: [
                          ["$$", "$$"],
                          ["\\[", "\\]"],
                        ],
                      },
                      "HTML-CSS": {
                        availableFonts: ["TeX"],
                        linebreaks: { automatic: true },
                        showMathMenu: false,
                      },
                    },
                    file_picker_types: "image media file",
                    htmlAllowedTags: [".*"],
                    htmlAllowedAttrs: [".*"],
                    extended_valid_elements: "*[.*]",
                    language_url: "/tinymce/langs/vi.js", // Đường dẫn tới file ngôn ngữ vi.js
                  }}
                  onEditorChange={(ev) => {
                    onChange(ev);
                  }}
                  {...otherFieldProps}
                />

                {errorField && (
                  <FormHelperText color="error">
                    {errorField?.message}
                  </FormHelperText>
                )}
              </FormControl>
            );
          }}
        />
      </AppFromLabelField>
    </>
  );
};

type AppFormControlTextEditorProps<T extends FieldValues> = {
  label?: string | ReactNode;
  control: Control<any>;
  name: FieldPath<T>;
  rules?: RegisterOptions;
  labelProps?: InputLabelProps;
  onChangeValueForm?: (value: string) => void;
  classNameContainer?: string;
  controlProps?: Omit<ControllerProps, "render" | "name" | "control">;
  defaultValue?: string;
  editorProps?: { disabled?: boolean };
  helper?: string | ReactNode;
  editorHeight?: number;
  action?: ReactNode;
  categoryType: CategoryTypeUploadEnum;
  ownerId?: string;
};

export default AppFormControlTextEditor;
